import { useEffect, useState, type FormEvent } from 'react';
import type {
  CreateReceiptApplicationRequest,
  CustomerReceiptDto,
  ReceiptApplicationDto,
  SalesInvoiceSummary,
} from '@hesabat/contracts';
import { Money } from '@hesabat/finance-core';
import { api, ProblemError, UnauthorizedError } from './api';
import { formatMoney, parseAmount } from './money';
import Loading from './Loading';

interface Props {
  companyId: string;
  currency: string;
  receipt: CustomerReceiptDto;
  issueLabels: Readonly<Record<string, string>>;
  defaultDate: string;
  onDone: (application: ReceiptApplicationDto) => void;
  onCancel: () => void;
  onSessionLost: () => void;
}

/**
 * تسوية دفعة مقدمة (باقى إيصال لم يُخصص) على فواتير العميل المفتوحة. مستند مستقل بقيده:
 * ينقل المبلغ من الدفعات المقدمة إلى ذمة الفاتورة. الحد هو الأقل من المتاح على الإيصال
 * ومتبقى الفاتورة، والخادم يتحقق من الاثنين ومن التواريخ.
 */
export default function ReceiptApplication({
  companyId,
  currency,
  receipt,
  issueLabels,
  defaultDate,
  onDone,
  onCancel,
  onSessionLost,
}: Props) {
  const [invoices, setInvoices] = useState<SalesInvoiceSummary[] | null>(null);
  const [applicationDate, setApplicationDate] = useState(defaultDate);
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [submitting, setSubmitting] = useState(false);
  const [issues, setIssues] = useState<{ code: string; message: string; lineIndex?: number }[]>([]);
  const [failure, setFailure] = useState<string | null>(null);

  function handleError(caught: unknown): void {
    if (caught instanceof UnauthorizedError) {
      onSessionLost();
      return;
    }
    if (caught instanceof ProblemError) {
      setIssues(caught.problem.issues ?? []);
      setFailure(
        caught.problem.issues?.length ? null : (caught.problem.detail ?? caught.problem.title),
      );
      return;
    }
    setFailure(caught instanceof Error ? caught.message : String(caught));
  }

  useEffect(() => {
    api
      .salesInvoices(companyId, receipt.customerId, true)
      .then((list) => setInvoices([...list].sort((a, b) => (a.dueDate < b.dueDate ? -1 : 1))))
      .catch(handleError);
  }, [companyId, receipt.customerId]);

  function touched(): void {
    setIdempotencyKey(crypto.randomUUID());
    setIssues([]);
    setFailure(null);
  }

  const rows = invoices ?? [];
  const values = rows.map((invoice) => parseAmount(amounts[invoice.id] ?? ''));
  const total = values.every((value) => value !== null) ? Money.sum(values as string[]) : null;
  const remaining = total !== null ? Money.of(receipt.unallocated).sub(total) : null;
  const ready =
    total !== null && total.isPositive() && remaining !== null && !remaining.isNegative();

  const chosenIndexes = values
    .map((value, index) => ({ index, value }))
    .filter((item) => item.value !== null && Money.of(item.value).isPositive())
    .map((item) => item.index);
  const issuesOfRow = (rowIndex: number) =>
    issues.filter((issue) => issue.lineIndex === chosenIndexes.indexOf(rowIndex));
  const generalIssues = issues.filter((issue) => issue.lineIndex === undefined);

  function suggestOldestFirst(): void {
    let left = Money.of(receipt.unallocated);
    const next: Record<string, string> = {};
    for (const invoice of rows) {
      if (!left.isPositive()) break;
      const open = Money.of(invoice.openBalance);
      const take = left.lt(open) ? left : open;
      next[invoice.id] = take.toFixed(2);
      left = left.sub(take);
    }
    setAmounts(next);
    touched();
  }

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!ready) return;
    setSubmitting(true);
    setIssues([]);
    setFailure(null);
    const body: CreateReceiptApplicationRequest = {
      receiptId: receipt.id,
      applicationDate,
      allocations: chosenIndexes.map((index) => ({
        invoiceId: rows[index]!.id,
        amount: values[index]!,
      })),
    };
    try {
      onDone(await api.createReceiptApplication(companyId, idempotencyKey, body));
    } catch (caught) {
      handleError(caught);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="panel" onSubmit={(event) => void submit(event)} noValidate>
      <div className="panel-head">
        <h2>
          تسوية الدفعة {receipt.number} — {receipt.customerName}
        </h2>
        <button type="button" className="ghost" onClick={onCancel}>
          إغلاق
        </button>
      </div>
      <p className="sub">
        المتاح للتسوية {formatMoney(receipt.unallocated)} {currency}. التسوية تنقل المبلغ من الدفعات
        المقدمة إلى الفاتورة، ولا تغيّر رصيد العميل.
      </p>
      <div className="form-row">
        <label>
          تاريخ التسوية
          <input
            type="date"
            value={applicationDate}
            onChange={(event) => {
              setApplicationDate(event.target.value);
              touched();
            }}
          />
        </label>
      </div>
      {invoices === null && !failure && <Loading />}
      {invoices?.length === 0 && <p className="sub">لا فواتير مفتوحة لهذا العميل بعد.</p>}
      {rows.length > 0 && (
        <>
          <div className="panel-head">
            <h3>الفواتير المفتوحة</h3>
            <button type="button" className="ghost" onClick={suggestOldestFirst}>
              اقترح التوزيع: الأقدم أولاً
            </button>
          </div>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>الفاتورة</th>
                  <th>الإصدار</th>
                  <th>المتبقى</th>
                  <th>يُسوّى</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((invoice, index) => (
                  <tr key={invoice.id}>
                    <td className="mono">{invoice.number}</td>
                    <td>{invoice.issueDate}</td>
                    <td className="num-cell">{formatMoney(invoice.openBalance)}</td>
                    <td>
                      <input
                        inputMode="decimal"
                        aria-label={'يُسوّى على ' + invoice.number}
                        value={amounts[invoice.id] ?? ''}
                        onChange={(event) => {
                          setAmounts((current) => ({
                            ...current,
                            [invoice.id]: event.target.value,
                          }));
                          touched();
                        }}
                      />
                      {issuesOfRow(index).map((issue, i) => (
                        <p key={i} className="error">
                          {issueLabels[issue.code] ?? issue.message}
                        </p>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="sub">
            يُسوّى {total ? formatMoney(total) : '—'} • يبقى من الدفعة{' '}
            {remaining ? formatMoney(remaining) : '—'} {currency}
            {remaining?.isNegative() ? ' — التسوية أكبر من المتاح' : ''}
          </p>
        </>
      )}
      {generalIssues.map((issue, i) => (
        <p key={i} className="error">
          {issueLabels[issue.code] ?? issue.message}
        </p>
      ))}
      {failure && <p className="error">{failure}</p>}
      <div className="form-actions">
        <button type="submit" className="primary" disabled={submitting || !ready}>
          {submitting ? 'جارى التسوية…' : 'تسوية الدفعة'}
        </button>
      </div>
    </form>
  );
}
